// pkg159b — cold-row "Open in mail" (mailto:, launches the OS default desktop client — wrong account for Ryan) → "Open in Outlook" deep link to outlook.office.com/mail/deeplink/compose with to/subject/body prefilled, new tab; composes in whichever M365 account the browser is signed into (mail@lawmighty.com); Copy stays as the fallback — delivered 2026-08-18
// pkg159 — HQ Marketing Emails page (D-159a-c): CSV upload with list-type declared at creation, lists table, per-list preview grid (server-rendered merge for every row, expandable bodies), per-row exclude, COLD path = per-row Copy + mailto + Mark sent (sends leave the M365 mailbox — the page never offers a bulk send for cold lists, mirroring the server's 403), EXISTING path = confirm-gated throttled Send via SendGrid with result summary, suppression manager card; templates filtered to the list's audience — delivered 2026-08-18
import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../utils/adminApi';

// Marketing Emails — LawMighty HQ.
//
// The two-audience posture is structural (D-159c): cold-prospect lists get a
// per-row copy assist so every cold email leaves Ryan's own mailbox at human
// volume; only existing-contact lists get the platform Send button, and the
// backend 403s any attempt to platform-send a cold list regardless of what
// this UI does. Suppression flags render live on every grid.

const font = { fontFamily: 'Arial, sans-serif' };
const NAVY_DEEP = '#0F1838';
const TERRA = '#B85042';

function Badge({ color, bg, children }) {
  return <span style={{ ...font, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, color, background: bg, marginLeft: 6, whiteSpace: 'nowrap' }}>{children}</span>;
}

export default function AdminHQMarketingEmailsPage() {
  const [templates, setTemplates] = useState([]);
  const [lists, setLists] = useState([]);
  const [suppression, setSuppression] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // upload form
  const [up, setUp] = useState({ name: '', list_type: 'cold_prospects', csv_text: '', file_name: '' });
  // open list state
  const [openList, setOpenList] = useState(null);   // { list, rows }
  const [tmplKey, setTmplKey] = useState('');
  const [existingBody, setExistingBody] = useState({ subject_override: '', message_body: '' });
  const [rendered, setRendered] = useState(null);   // [{row_id,...}]
  const [expanded, setExpanded] = useState({});     // row_id -> bool
  const [supEmail, setSupEmail] = useState('');

  const load = useCallback(() => {
    Promise.all([
      adminApi.hqMarketingTemplates(),
      adminApi.hqMarketingLists(),
      adminApi.hqMarketingSuppression(),
    ]).then(([t, l, s]) => {
      setTemplates(t.templates || []); setLists(l.lists || []); setSuppression(s.suppression || []);
      setLoading(false);
    }).catch(err => { setError(err.message); setLoading(false); });
  }, []);
  useEffect(() => { load(); }, [load]);

  async function act(key, fn, ok) {
    setBusy(key); setError(''); setNotice('');
    try { const r = await fn(); if (ok) setNotice(ok(r)); return r; }
    catch (err) { setError(err.message); return null; }
    finally { setBusy(''); }
  }

  // ── upload ──
  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setUp(p => ({ ...p, csv_text: String(reader.result || ''), file_name: f.name, name: p.name || f.name.replace(/\.csv$/i, '') }));
    reader.readAsText(f);
  };
  const createList = () => act('upload',
    () => adminApi.hqMarketingCreateList({ name: up.name, list_type: up.list_type, csv_text: up.csv_text })
      .then(r => { setUp({ name: '', list_type: up.list_type, csv_text: '', file_name: '' }); load(); return r; }),
    r => `Imported ${r.imported} contacts` + (r.flagged_suppressed ? ` — ${r.flagged_suppressed} flagged as suppressed` : '')
      + (r.skipped_invalid && r.skipped_invalid.length ? ` — skipped ${r.skipped_invalid.length} invalid` : '') + '.');

  // ── open + render ──
  const openListById = async (id) => {
    setRendered(null); setExpanded({});
    const r = await act('open-' + id, () => adminApi.hqMarketingList(id), null);
    if (r) {
      setOpenList(r);
      const audience = r.list.list_type === 'cold_prospects' ? 'cold' : 'existing';
      const first = templates.find(t => t.audience === audience);
      setTmplKey(first ? first.key : '');
    }
  };
  const audience = openList ? (openList.list.list_type === 'cold_prospects' ? 'cold' : 'existing') : null;
  const audienceTemplates = templates.filter(t => t.audience === audience);
  const doRender = () => act('render',
    () => adminApi.hqMarketingRender({ list_id: openList.list.id, template: tmplKey, ...existingBody })
      .then(r => { setRendered(r.rendered || []); return r; }),
    r => `Rendered ${r.rendered.length} previews.`);

  const toggleExclude = async (row) => {
    await act('excl-' + row.row_id, () => adminApi.hqMarketingRowExclude(row.row_id, !row.excluded), null);
    doRender();
  };

  // ── cold assists ──
  const copyEmail = async (r) => {
    const text = `To: ${r.email}\nSubject: ${r.subject}\n\n${r.body}`;
    try { await navigator.clipboard.writeText(text); setNotice(`Copied email for ${r.email}.`); }
    catch { setError('Clipboard blocked — expand the row and copy manually.'); }
  };
  // Outlook on the web compose deep link — opens in the M365 account the
  // browser is signed into (mail@lawmighty.com), not the OS default client.
  const owaHref = (r) => 'https://outlook.office.com/mail/deeplink/compose'
    + `?to=${encodeURIComponent(r.email)}&subject=${encodeURIComponent(r.subject)}&body=${encodeURIComponent(r.body)}`;
  const markSent = (r) => act('sent-' + r.row_id,
    () => adminApi.hqMarketingMarkSent(r.row_id, tmplKey).then(x => { doRender(); return x; }),
    () => `Recorded ${r.email} as sent (manual).`);

  // ── existing send ──
  const sendAll = () => {
    const pending = (rendered || []).filter(r => !r.excluded && !r.suppressed && !r.sent_at).length;
    if (!window.confirm(`Send "${(audienceTemplates.find(t => t.key === tmplKey) || {}).label}" to ${pending} contacts via SendGrid? Suppressed and excluded rows are skipped automatically.`)) return;
    act('send',
      () => adminApi.hqMarketingSend({ list_id: openList.list.id, template: tmplKey, ...existingBody }).then(x => { doRender(); return x; }),
      r2 => `Sent ${r2.sent}` + (r2.skipped_suppressed ? `, skipped ${r2.skipped_suppressed} suppressed` : '')
        + (r2.failed && r2.failed.length ? `, FAILED: ${r2.failed.join(', ')}` : '') + (r2.remaining_cap_note ? ` — ${r2.remaining_cap_note}` : '') + '.');
  };

  // ── suppression ──
  const addSuppression = () => act('sup-add',
    () => adminApi.hqMarketingSuppress(supEmail).then(x => { setSupEmail(''); load(); if (rendered) doRender(); return x; }),
    () => 'Added to suppression list.');
  const removeSuppression = (id, email) => {
    if (!window.confirm(`Remove ${email} from the suppression list? Only do this for typos — never for a real unsubscribe.`)) return;
    act('sup-del', () => adminApi.hqMarketingUnsuppress(id).then(x => { load(); if (rendered) doRender(); return x; }), () => 'Removed.');
  };

  if (loading) return <div style={{ padding: 40, ...font, color: '#888' }}>Loading...</div>;

  return (
    <div>
      <h1 className="page-title">Marketing Emails</h1>
      <p className="page-subtitle">
        Upload a contact CSV, preview every merged email, then dispatch by audience: cold prospects are copied
        row-by-row and sent from the M365 mailbox (the platform never bulk-sends cold email); existing contacts
        can be sent through the platform with automatic suppression checks. Every send is logged.
      </p>

      {error  && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      {/* Upload */}
      <div className="card">
        <div style={{ ...font, fontSize: 14, fontWeight: 700, color: NAVY_DEEP, marginBottom: 8 }}>New contact list</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ ...font, fontSize: 12, color: '#444' }}>CSV file (needs an &ldquo;email&rdquo; column; first_name / firm_name / extras become merge fields)<br />
            <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ ...font, fontSize: 12, marginTop: 4 }} />
          </label>
          <label style={{ ...font, fontSize: 12, color: '#444' }}>List name<br />
            <input type="text" value={up.name} onChange={e => setUp(p => ({ ...p, name: e.target.value }))}
              style={{ ...font, fontSize: 13, padding: '6px 8px', marginTop: 4, width: 200 }} />
          </label>
          <label style={{ ...font, fontSize: 12, color: '#444' }}>List type<br />
            <select value={up.list_type} onChange={e => setUp(p => ({ ...p, list_type: e.target.value }))}
              style={{ ...font, fontSize: 13, padding: '6px 8px', marginTop: 4 }}>
              <option value="cold_prospects">Cold prospects (manual sends from M365)</option>
              <option value="existing_contacts">Existing contacts (platform send allowed)</option>
            </select>
          </label>
          <button className="btn btn-sm btn-primary" disabled={busy === 'upload' || !up.csv_text || !up.name.trim()} onClick={createList}>
            {busy === 'upload' ? 'Importing…' : 'Import list'}
          </button>
        </div>
        {up.file_name && <div style={{ ...font, fontSize: 11, color: '#888', marginTop: 6 }}>Loaded: {up.file_name}</div>}
        <div style={{ ...font, fontSize: 11, color: '#999', marginTop: 8 }}>
          The list type is permanent — it decides whether the platform is ever allowed to send to this list.
        </div>
      </div>

      {/* Lists */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ ...font, fontSize: 14, fontWeight: 700, color: NAVY_DEEP, marginBottom: 8 }}>Lists</div>
        {lists.length === 0 && <div style={{ ...font, fontSize: 13, color: '#888' }}>No lists yet.</div>}
        {lists.map(l => (
          <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #eee' }}>
            <div style={{ ...font, fontSize: 13 }}>
              <strong>{l.name}</strong>
              {l.list_type === 'cold_prospects'
                ? <Badge color="#7c5200" bg="#fdf6e6">COLD — manual only</Badge>
                : <Badge color="#155724" bg="#d4edda">EXISTING</Badge>}
              <span style={{ color: '#888', marginLeft: 8 }}>{l.total} contacts · {l.sent} sent · {l.excluded} excluded</span>
            </div>
            <button className="btn btn-sm" onClick={() => openListById(l.id)}>Open</button>
          </div>
        ))}
      </div>

      {/* Open list */}
      {openList && (
        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ ...font, fontSize: 14, fontWeight: 700, color: NAVY_DEEP }}>
              {openList.list.name}
              {audience === 'cold'
                ? <Badge color="#7c5200" bg="#fdf6e6">COLD — send each from M365</Badge>
                : <Badge color="#155724" bg="#d4edda">EXISTING — platform send allowed</Badge>}
            </div>
            <button className="btn btn-sm" onClick={() => { setOpenList(null); setRendered(null); }}>Close</button>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}>
            <label style={{ ...font, fontSize: 12, color: '#444' }}>Template<br />
              <select value={tmplKey} onChange={e => { setTmplKey(e.target.value); setRendered(null); }}
                style={{ ...font, fontSize: 13, padding: '6px 8px', marginTop: 4 }}>
                {audienceTemplates.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </label>
            {audience === 'existing' && (<>
              <label style={{ ...font, fontSize: 12, color: '#444' }}>Subject (optional override)<br />
                <input type="text" value={existingBody.subject_override}
                  onChange={e => setExistingBody(p => ({ ...p, subject_override: e.target.value }))}
                  style={{ ...font, fontSize: 13, padding: '6px 8px', marginTop: 4, width: 260 }} />
              </label>
              <label style={{ ...font, fontSize: 12, color: '#444', flexBasis: '100%' }}>Message body (merge fields like {'{{first_name}}'} and {'{{firm_name}}'} work here)<br />
                <textarea value={existingBody.message_body}
                  onChange={e => setExistingBody(p => ({ ...p, message_body: e.target.value }))}
                  rows={5} style={{ ...font, fontSize: 13, padding: '6px 8px', marginTop: 4, width: '100%', boxSizing: 'border-box' }} />
              </label>
            </>)}
            <button className="btn btn-sm" disabled={busy === 'render' || !tmplKey} onClick={doRender}>
              {busy === 'render' ? 'Rendering…' : 'Preview all'}
            </button>
            {audience === 'existing' && rendered && (
              <button className="btn btn-sm btn-primary" disabled={busy === 'send'} onClick={sendAll}>
                {busy === 'send' ? 'Sending…' : 'Send to list (SendGrid)'}
              </button>
            )}
          </div>

          {rendered && (
            <div style={{ marginTop: 12 }}>
              <div style={{ ...font, fontSize: 11, color: '#888', marginBottom: 6 }}>
                {rendered.length} rows · suppressed and excluded rows are shown but never sent
                {audience === 'cold' && ' · Copy each email, send it from the M365 mailbox, then Mark sent'}
              </div>
              {rendered.map(r => (
                <div key={r.row_id} style={{ borderBottom: '1px solid #eee', padding: '7px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                    <div style={{ ...font, fontSize: 13, minWidth: 0 }}>
                      <strong>{r.email}</strong>
                      {r.suppressed && <Badge color="#721c24" bg="#f8d7da">SUPPRESSED</Badge>}
                      {r.excluded && <Badge color="#666" bg="#eee">excluded</Badge>}
                      {r.sent_at && <Badge color="#155724" bg="#d4edda">sent</Badge>}
                      <div style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.subject}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button className="btn btn-sm" onClick={() => setExpanded(p => ({ ...p, [r.row_id]: !p[r.row_id] }))}>
                        {expanded[r.row_id] ? 'Hide' : 'View'}
                      </button>
                      <button className="btn btn-sm" onClick={() => toggleExclude(r)}>{r.excluded ? 'Include' : 'Exclude'}</button>
                      {audience === 'cold' && !r.suppressed && !r.excluded && (<>
                        <button className="btn btn-sm" onClick={() => copyEmail(r)}>Copy</button>
                        <a className="btn btn-sm" href={owaHref(r)} target="_blank" rel="noopener noreferrer">Open in Outlook</a>
                        {!r.sent_at && (
                          <button className="btn btn-sm" disabled={busy === 'sent-' + r.row_id} onClick={() => markSent(r)}>Mark sent</button>
                        )}
                      </>)}
                    </div>
                  </div>
                  {expanded[r.row_id] && (
                    r.body
                      ? <pre style={{ ...font, fontSize: 12, whiteSpace: 'pre-wrap', background: '#faf8f3', border: '1px solid #eee', borderRadius: 4, padding: 10, marginTop: 8 }}>{r.body}</pre>
                      : <iframe title={'preview-' + r.row_id} srcDoc={r.html}
                          style={{ width: '100%', height: 380, border: '1px solid #ddd', borderRadius: 4, marginTop: 8, background: '#fff' }} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Suppression */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ ...font, fontSize: 14, fontWeight: 700, color: NAVY_DEEP, marginBottom: 4 }}>Suppression list</div>
        <div style={{ ...font, fontSize: 11, color: '#888', marginBottom: 8 }}>
          Anyone who replies &ldquo;unsubscribe&rdquo; goes here the same day. Checked automatically on every platform
          send and every mark-sent; imports flag matches on upload. Opt-outs must be honored within 10 business days.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="email" placeholder="email to suppress" value={supEmail} onChange={e => setSupEmail(e.target.value)}
            style={{ ...font, fontSize: 13, padding: '6px 8px', width: 240 }} />
          <button className="btn btn-sm btn-primary" disabled={busy === 'sup-add' || !supEmail.trim()} onClick={addSuppression}>Add</button>
        </div>
        <div style={{ marginTop: 8 }}>
          {suppression.map(s => (
            <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f2f0ea' }}>
              <span style={{ ...font, fontSize: 12 }}>{s.email}{s.reason ? <span style={{ color: '#999' }}> — {s.reason}</span> : null}</span>
              <button className="btn btn-sm" onClick={() => removeSuppression(s.id, s.email)} style={{ color: TERRA }}>Remove</button>
            </div>
          ))}
          {suppression.length === 0 && <div style={{ ...font, fontSize: 12, color: '#999' }}>Empty — import your campaign tracker's suppression list before the first send.</div>}
        </div>
      </div>
    </div>
  );
}
